import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Mail, Lightbulb, Info, ListOrdered } from 'lucide-react';
import { bookingUrl } from '../data/mock';

const slugify = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const splitSections = (content) => {
  const sections = [];
  let current = { title: null, blocks: [] };
  content.forEach((b) => {
    if (b.type === 'h2') { sections.push(current); current = { title: b.text, blocks: [] }; }
    else current.blocks.push(b);
  });
  sections.push(current);
  let disclaimer = null;
  sections.forEach((s) => {
    s.blocks = s.blocks.filter((b) => {
      if (b.type === 'p' && b.text.startsWith('Ce contenu est fourni à titre informatif')) { disclaimer = b.text; return false; }
      return true;
    });
  });
  return { sections: sections.filter((s) => s.title || s.blocks.length), disclaimer };
};

const Paragraph = ({ text, lead }) => (
  lead
    ? <p className="text-lg md:text-xl text-[#010c23] leading-relaxed font-light first-letter:font-serif first-letter:text-6xl first-letter:leading-[0.8] first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:text-[#ff7300]">{text}</p>
    : <p className="text-[#010c23]/75 leading-relaxed mt-4 text-base">{text}</p>
);

const List = ({ items }) => (
  <ul className="mt-4 space-y-2 pl-1">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-[#010c23]/75 leading-relaxed"><span className="mt-[11px] h-1.5 w-1.5 flex-shrink-0 bg-[#ff7300]" />{item}</li>
    ))}
  </ul>
);

const Blocks = ({ blocks, leadIndex }) => blocks.map((b, i) => {
  const key = `${b.type}-${i}-${(b.text || b.items?.[0] || '').slice(0, 20)}`;
  if (b.type === 'ul') return <List key={key} items={b.items} />;
  if (b.type === 'callout') return <div key={key} className="my-8 p-6 border-l-2 border-[#ff7300] bg-[#fbf9f5] text-[#010c23]/85 italic font-serif text-lg leading-relaxed">{b.text}</div>;
  return <Paragraph key={key} text={b.text} lead={i === leadIndex} />;
});

const AccompanyCard = ({ section, id }) => (
  <section id={id} className="mt-14 scroll-mt-32 bg-[#010c23] text-white p-8 md:p-10 relative overflow-hidden" data-testid="article-accompany">
    <span className="absolute -right-6 -top-8 font-serif text-[160px] leading-none text-white/5 select-none">O</span>
    <p className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Notre accompagnement</p>
    <h2 className="font-serif text-2xl md:text-3xl font-light mt-3">{section.title}</h2>
    <div className="mt-5 space-y-4 text-white/80 leading-relaxed">{section.blocks.map((b) => <p key={b.text.slice(0, 30)}>{b.text}</p>)}</div>
    <div className="mt-8 flex flex-wrap gap-4">
      <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-primary"><Calendar size={16} /> Prendre rendez-vous en ligne</a>
      <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white text-sm font-medium tracking-wide hover:border-[#ff7300] hover:text-[#ff7300] transition-colors">Nous contacter <Mail size={16} /></Link>
    </div>
  </section>
);

const ConclusionCard = ({ section, id }) => (
  <section id={id} className="mt-14 scroll-mt-32 border border-[#ff7300]/40 bg-[#fbf9f5] p-8" data-testid="article-conclusion">
    <div className="flex items-center gap-3 text-[#ff7300]"><Lightbulb size={18} /><span className="text-[11px] tracking-[0.35em] uppercase font-medium">À retenir</span></div>
    <div className="mt-4 space-y-4 font-serif text-lg md:text-xl text-[#010c23] leading-relaxed">{section.blocks.map((b) => <p key={b.text.slice(0, 30)}>{b.text}</p>)}</div>
  </section>
);

const Section = ({ section, id, leadIndex }) => (
  <section id={id} className="mt-14 scroll-mt-32">
    <div className="mb-5">
      <span className="block h-px w-12 bg-[#ff7300] mb-4" />
      <h2 className="font-serif text-2xl md:text-3xl text-[#010c23] font-light leading-snug">{section.title}</h2>
    </div>
    <Blocks blocks={section.blocks} leadIndex={leadIndex} />
  </section>
);

const Toc = ({ items }) => (
  <nav aria-label="Sommaire" data-testid="article-toc">
    <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#010c23]/50 mb-4"><ListOrdered size={14} /> Sommaire</div>
    <ol className="space-y-2.5 border-l border-black/10">
      {items.map((it) => (
        <li key={it.id}><a href={`#${it.id}`} className="block -ml-px pl-4 border-l-2 border-transparent text-sm text-[#010c23]/70 hover:text-[#ff7300] hover:border-[#ff7300] transition-colors leading-snug">{it.title}</a></li>
      ))}
    </ol>
  </nav>
);

const ArticleBody = ({ article }) => {
  const { sections, disclaimer } = splitSections(article.content);
  const numbered = sections.filter((s) => s.title);
  const toc = numbered.map((s) => ({ id: slugify(s.title), title: s.title }));
  const firstWithP = sections.findIndex((s) => s.blocks.some((b) => b.type === 'p'));
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 lg:grid lg:grid-cols-12 lg:gap-12">
        <aside className="hidden lg:block lg:col-span-3"><div className="sticky top-32"><Toc items={toc} /></div></aside>
        <details className="lg:hidden mb-8 border border-black/10 bg-[#fbf9f5] p-4 group" data-testid="article-toc-mobile">
          <summary className="cursor-pointer text-[11px] tracking-[0.3em] uppercase text-[#010c23]/70 flex items-center gap-2 list-none"><ListOrdered size={14} /> Sommaire</summary>
          <div className="mt-4"><Toc items={toc} /></div>
        </details>
        <div className="lg:col-span-9 max-w-[760px] prose-content justify-text">
          {sections.map((s, i) => {
            const leadIndex = i === firstWithP ? s.blocks.findIndex((b) => b.type === 'p') : -1;
            if (!s.title) return <div key="intro"><Blocks blocks={s.blocks} leadIndex={leadIndex} /></div>;
            const id = slugify(s.title);
            if (s.title.startsWith('Comment Ometis')) return <AccompanyCard key={id} section={s} id={id} />;
            if (s.title === 'Conclusion') return <ConclusionCard key={id} section={s} id={id} />;
            return <Section key={id} section={s} id={id} leadIndex={leadIndex} />;
          })}
          {disclaimer && (
            <div className="mt-14 flex items-start gap-3 p-5 border border-black/10 text-xs text-[#010c23]/60 leading-relaxed" data-testid="article-disclaimer">
              <Info size={16} className="mt-0.5 flex-shrink-0 text-[#010c23]/40" />
              <p><span className="font-medium text-[#010c23]/80">Information importante — </span>{disclaimer}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ArticleBody;
